import { useRef, useState, type FormEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { displayTitle } from '../../../lib/displayName'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { BrandMascot, EyeIcon, EyeOffIcon } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { ApiError } from '../../../lib/api'
import { normalizeRoleParam } from './roleParam'
import type { ChowRole } from '../../../lib/actor'
import { useAuth } from './AuthContext'
import { getPendingAction, executePendingAction } from './pendingAction'
import { PublicFrame } from './PublicFrame'
import { RoleToggle } from './RoleToggle'

/**
 * The warm cream behind the membership panel. Mixed from `warning` onto
 * `bgSurface` because the palette carries no warm tint of its own.
 */
const WARM_CREAM = `color-mix(in srgb, ${minimalVars.color.warning} 10%, ${minimalVars.color.bgSurface})`

const Style = {
  Center: styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${minimalVars.space.md};
    padding: ${minimalVars.space.lg} ${minimalVars.space.md};
    width: 100%;
    box-sizing: border-box;
  `,
  // Off-screen rather than display:none: bots skip inputs that are plainly
  // hidden, but fill in ones that are only positioned out of view.
  Trap: styled.div`
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  `,
  Brand: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${minimalVars.space.xs};
    text-align: center;
  `,
  Mark: styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
  `,
  Title: styled.h1`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    /* The form title stays at 1.6rem: h1Size reaches 40px and wraps it on phones. */
    font-size: 1.6rem;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: ${minimalVars.color.textPrimary};
  `,
  Sub: styled.p`
    margin: 0;
    max-width: 30ch;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.bodySize};
    line-height: 1.45;
  `,
  Card: styled.div`
    width: 100%;
    max-width: 360px;
    display: grid;
    gap: ${minimalVars.space.md};
  `,
  Conversion: styled.aside`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 96px;
    gap: ${minimalVars.space.xs};
    padding: ${minimalVars.space.xs};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: 18px;
    background: linear-gradient(135deg, ${WARM_CREAM} 0%, ${minimalVars.color.bgSurface} 62%);
    box-shadow: 0 10px 24px rgba(23, 39, 62, 0.08);
  `,
  ConversionCopy: styled.div`
    display: grid;
    gap: 8px;
    align-content: start;
    min-width: 0;
  `,
  Eyebrow: styled.span`
    color: ${minimalVars.color.brand};
    font-size: ${minimalVars.typography.metaSize};
    font-weight: 900;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  `,
  ConversionTitle: styled.strong`
    color: ${minimalVars.color.textPrimary};
    font-family: ${minimalVars.typography.displayFamily};
    /* Panel titles stay at 1.05rem: h2Size is 22px and wraps them beside the 96px image. */
    font-size: 1.05rem;
    line-height: 1.16;
  `,
  ConversionSub: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.45;
  `,
  ConversionPrice: styled.div`
    color: ${minimalVars.color.brand};
    font-weight: 900;
    font-variant-numeric: tabular-nums;
    span {
      color: ${minimalVars.color.textTertiary};
      font-size: ${minimalVars.typography.captionSize};
      font-weight: 700;
    }
  `,
  ConversionList: styled.ul`
    display: grid;
    gap: 5px;
    margin: 0;
    padding: 0;
    list-style: none;
    li {
      display: grid;
      grid-template-columns: 16px minmax(0, 1fr);
      gap: 6px;
      color: ${minimalVars.color.textSecondary};
      font-size: ${minimalVars.typography.captionSize};
      line-height: 1.32;
    }
    svg {
      color: ${minimalVars.color.success};
      margin-top: 1px;
    }
  `,
  ConversionImage: styled.img`
    width: 96px;
    height: 100%;
    min-height: 136px;
    object-fit: cover;
    border-radius: 14px;
    border: 1px solid ${minimalVars.color.borderSubtle};
    background: ${minimalVars.color.bgSurfaceAlt};
  `,
  Form: styled.form`
    display: grid;
    gap: ${minimalVars.space.sm};
  `,
  Switch: styled.button`
    border: 0;
    background: transparent;
    color: ${minimalVars.color.textSecondary};
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    cursor: pointer;
    padding: ${minimalVars.space.xs};
  `,
  Strong: styled.span`
    color: ${minimalVars.color.brand};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Reveal: styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    color: ${minimalVars.color.textTertiary};
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover,
    &:focus-visible {
      color: ${minimalVars.color.textSecondary};
    }
  `,
}

// Turn a thrown auth error into a message that names what actually went wrong,
// so a rejected sign-in reads as "wrong credentials" rather than a vague fault.
const messageForAuthError = (err: unknown, isRegister: boolean): string => {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      return 'Those details don’t match an account. Check your email or phone number and password.'
    }
    if (err.status === 409) {
      return isRegister
        ? 'You already have an account with this email or phone number. Sign in instead.'
        : 'This phone number is on more than one account. Sign in with your email instead.'
    }
    if (err.status === 400) {
      return 'Enter a full email address or phone number, for example 0803 123 4567.'
    }
    if (err.status === 429) {
      return 'Too many attempts. Wait a moment, then try again.'
    }
  }
  return isRegister
    ? 'We could not create your account. Please try again.'
    : 'We could not sign you in. Please try again.'
}

// A field people never see, reach by keyboard or hear read out; only scripts
// that fill in every input give it a value (the server's form_guard.go).
const Honeypot = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <Style.Trap aria-hidden="true">
    <label>
      Leave this field empty
      <input
        type="text"
        name="contact_note"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  </Style.Trap>
)

export const AuthPage = ({ mode }: { mode: 'login' | 'register' }) => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { login, register } = useAuth()

  const returnTo = params.get('returnTo') || ''
  const hasMealPlanningIntent = Boolean(params.get('plan')) || ['/mealplan', '/subscriptions', '/memberships', '/plan/'].some((path) => returnTo.startsWith(path))
  const [role, setRole] = useState<ChowRole>(
    hasMealPlanningIntent ? 'customer' : normalizeRoleParam(params.get('role')) ?? 'customer',
  )
  const [displayName, setDisplayName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [pending] = useState(() => getPendingAction())
  // Bot form guard: when the form appeared, and a honeypot only scripts fill in.
  const openedAt = useRef(performance.now())
  const [contactNote, setContactNote] = useState('')

  const isRegister = mode === 'register'
  const conversionDestination = role === 'customer' && hasMealPlanningIntent ? '/mealplan' : null
  const switchTarget = () => {
    const next = new URLSearchParams(params)
    next.set('role', role)
    const query = next.toString()
    return `${isRegister ? '/login' : '/register'}${query ? `?${query}` : ''}`
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!identifier.trim() || !password.trim() || (isRegister && !displayName.trim())) {
      setError(isRegister ? 'Fill in every field to create your account.' : 'Enter your email or phone number, and your password.')
      return
    }
    setError('')
    setBusy(true)
    try {
      const guard = { contactNote, formMs: performance.now() - openedAt.current }
      let authedUser
      if (isRegister) {
        // A kitchen name is a storefront: store it the way every card shows it.
        authedUser = await register({ displayName: role === 'chef' ? displayTitle(displayName) : displayName.trim(), identifier, password, role, guard })
      } else {
        authedUser = await login({ identifier, password, role, guard })
      }

      // A new kitchen goes straight to setup: location, photo and phone are
      // what stand between a fresh account and a first order.
      if (isRegister && role === 'chef') {
        navigate('/welcome', { replace: true })
        return
      }
      const destination = await executePendingAction(authedUser.profileId || '')
      // Discard returnTo if it was merely an unauthenticated bounce to /profile
      const cleanReturnTo = returnTo && !returnTo.startsWith('/profile') ? returnTo : null
      navigate(destination || conversionDestination || cleanReturnTo || '/dashboard', { replace: true })
    } catch (err) {
      setError(messageForAuthError(err, isRegister))
      setBusy(false)
    }
  }

  const roleNoun = role === 'chef' ? 'chef' : 'customer'

  return (
    <PublicFrame>
      <Style.Center>
        <Style.Brand>
          <Style.Mark>
            <BrandMascot size={104} />
          </Style.Mark>
          <Style.Title>{isRegister ? 'Create your account' : 'Welcome back'}</Style.Title>
          <Style.Sub>
            {pending?.dish?.name
              ? `Sign in or create an account to add ${displayTitle(pending.dish.name)} to your order.`
              : isRegister
              ? role === 'chef'
                ? 'Sell the food you already cook to people near you.'
                : 'Homemade food from kitchens near you, ready for pickup.'
              : 'Sign in to see your orders and favourite kitchens.'}
          </Style.Sub>
        </Style.Brand>

        <Style.Card>
          <RoleToggle value={role} onChange={setRole} />

          <Style.Form onSubmit={submit}>
            <Honeypot value={contactNote} onChange={setContactNote} />
            <AnimatePresence initial={false} mode="popLayout">
              {isRegister ? (
                <motion.div
                  key="name"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* A chef's display name IS the kitchen name: it is what
                      SetVendorProfile copies onto the vendor record, so it is
                      the name customers see on the dish card, in the cart and
                      on the order. Asking a chef for "Name" got personal names
                      onto storefronts. */}
                  <Input
                    label={role === 'chef' ? 'Kitchen name' : 'Name'}
                    placeholder={role === 'chef' ? "For example, Amara's Kitchen" : 'Your name'}
                    hint={role === 'chef' ? 'Customers see this on every dish and order.' : undefined}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    autoComplete={role === 'chef' ? 'organization' : 'name'}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
            <Input
              label="Email or phone number"
              type="text"
              placeholder="you@example.com or 0803 123 4567"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
            />
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              error={error || undefined}
              rightIcon={
                <Style.Reveal
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon size="sm" /> : <EyeIcon size="sm" />}
                </Style.Reveal>
              }
            />
            <Button type="submit" isLoading={busy}>
              {isRegister ? `Create ${roleNoun} account` : 'Sign in'}
            </Button>
          </Style.Form>

          <Style.Switch
            type="button"
            onClick={() => navigate(switchTarget())}
          >
            {isRegister ? (
              <>
                Already have an account? <Style.Strong>Sign in</Style.Strong>
              </>
            ) : (
              <>
                New to ChooseChow? <Style.Strong>Create an account</Style.Strong>
              </>
            )}
          </Style.Switch>
        </Style.Card>
      </Style.Center>
    </PublicFrame>
  )
}
